import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type SessionService from 'walden-trace-app/services/session';
import type { IntlService } from 'ember-intl';
import type UserService from 'walden-trace-app/services/user';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type AppModel = Resolved<ReturnType<AppRoute['model']>>;

export default class AppRoute extends Route {
  @service('session') declare sessionService: SessionService;
  @service('router') declare routerService: RouterService;
  @service('intl') declare intlService: IntlService;
  @service('user') declare userService: UserService;

  async beforeModel() {
    // Intercepting the immediate redirect to the login page on Safari, because when scanning the QR code with your iPhone camera app, the URL is opened in a sandbox / incognito environment
    // If we immediately redirect the user will have to log in every time. By redirecting an intermeditate page first, we can prompt the user to open the URL in their regular browser where they are already logged in.
    // Because opening this page in the Safari app will only transfer the information of the URL, we place the attempted URL in the queryParams
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const loggedIn = await this.sessionService.isAuthenticated();
    if (isSafari && !loggedIn) {
      const attemptedURL = window.location.pathname + window.location.search;
      this.sessionService.setAttemptedUrl(attemptedURL);
      await this.routerService.transitionTo('login', {
        queryParams: { attemptedURL },
      });
      return;
    }

    if (!this.sessionService.isGuest) {
      return this.sessionService.redirectToLoginIfNotAuthenticated();
    }
  }

  async model() {
    const user = await this.userService.getUser();

    if (user?.preferredLanguage) {
      this.intlService.setLocale(user?.preferredLanguage);
    }
    return {
      user,
    };
  }

  async afterModel() {
    const user = await this.userService.getUser();

    if (user !== null && !user.privacyPolicyAccepted) {
      const requestedRouteName = window.location.pathname + window.location.search;

      this.routerService.transitionTo('privacy-policy', {
        queryParams: { redirectURL: requestedRouteName },
      });
    }
  }
}
